/**
 * PlanRoomHomeCard — compact "latest issued set" card for Project Home / Info.
 *
 * Fetches `GET /projects/{pid}/plan-room/home-card` and renders the latest
 * current set with its first sheet's thumbnail. Click-through opens the
 * Plan Room. Designed to slot into the upcoming Home redesign.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PackageCheck, Inbox, ArrowRight, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlanRoomHomeCard({ projectId, darkMode = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    axios.get(`${API}/projects/${projectId}/plan-room/home-card`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return null;
  if (!data) return null;

  const { latest_set: latest, first_sheet_thumbnail: thumb, unfiled_count, drawings_count } = data;
  const hasContent = drawings_count > 0 || unfiled_count > 0;

  return (
    <button
      type="button"
      onClick={() => navigate(`/projects/${projectId}/drawings`)}
      className={`w-full text-left rounded-lg overflow-hidden border transition-colors flex ${darkMode
        ? 'border-[#2A2A2A] bg-[#141414] hover:bg-[#1A1A1A]'
        : 'border-[#E5E2DD] bg-white hover:bg-[#FAF9F7]'}`}
      data-testid="plan-room-home-card"
    >
      <div className="w-24 aspect-[4/5] flex-shrink-0 bg-gray-100 flex items-center justify-center">
        {thumb ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${thumb}`} alt="" className="w-full h-full object-cover" />
        ) : (
          <FileText className="w-8 h-8 text-gray-400" />
        )}
      </div>
      <div className="flex-1 p-3 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2">
            <PackageCheck className={`w-4 h-4 ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`} />
            <span className={`text-xs uppercase tracking-wide font-semibold ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
              Plan Room
            </span>
          </div>
          {latest ? (
            <div className="mt-1">
              <p className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-[#1A1918]'}`}>
                {latest.name}
              </p>
              <p className="text-xs text-muted-foreground">
                Rev {latest.version_refs?.[0]?.revision_code || '—'} · {latest.phase} · Issued {latest.issue_date}
              </p>
            </div>
          ) : (
            <p className={`text-sm mt-1 ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
              {hasContent ? 'No issued sets yet.' : 'No drawings yet.'}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            {unfiled_count > 0 && (
              <Badge className="text-[10px] bg-[#70170F] text-white flex items-center gap-1">
                <Inbox className="w-3 h-3" /> {unfiled_count} unfiled
              </Badge>
            )}
            <Badge variant="outline" className="text-[10px]">
              {drawings_count} sheet{drawings_count === 1 ? '' : 's'}
            </Badge>
          </div>
          <ArrowRight className={`w-4 h-4 ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`} />
        </div>
      </div>
    </button>
  );
}
